'use strict';

/**
 *
 * Response class allow to retrieve easily the response from the api
 *
 * @example var response = new Response();
 * @module core/response
 * @author Florian Kroockmann <florian.kroockmann@lp-digital.fr>
 */
 module.exports = class Response {
  /**
   * Constructor initialize the data of the request
   */
  constructor() {
    this.headers = {};
    this.data = {};
    this.rawData = '';
    this.status = 200;
    this.statusText = '';
    this.errorText = '';
  }

  /**
   * return all headers of Response
   *
   * @function getHeaders
   * @memberof module:core/response
   * @example response.getHeaders();
   * @returns {object} Headers
   */
  getHeaders() {
    return this.headers;
  }

  /**
   * Return one header by key
   *
   * @function getHeader
   * @memberof module:core/response
   * @example response.getHeader('Content-Type');
   * @param {string} key
   * @returns {string | null} header
   */
  getHeader(key) {
    return this.headers[key] || this.headers[key.toLowerCase()] ||null;
  }

  /**
   * Return data, if data not set it
   * will return data raw
   *
   * @function getData
   * @example response.getData();
   * @memberof module:core/response
   * @returns {Mixed} data
   */
  getData() {
    return '' === this.data ? this.rawData : this.data;
  }

  /**
   * Return raw datas
   *
   * @function getRawData
   * @example response.getRawData();
   * @memberof module:core/response
   * @returns {string} raw data
   */
  getRawData() {
    return this.rawData;
  }

  /**
   * Return status code
   *
   * @function getStatus
   * @example response.getStatus();
   * @memberof module:core/response
   * @returns {number} status
   */
  getStatus() {
    return this.status;
  }

  /**
   * Return status text
   *
   * @function getStatusText
   * @example response.getStatusText();
   * @memberof module:core/response
   * @returns {string} status text
   */
  getStatusText() {
    return this.statusText;
  }

  /**
   * Return error text
   *
   * @function getErrorText
   * @example response.getErrorText();
   * @memberof module:core/response
   * @returns {string} error text
   */
  getErrorText() {
    return this.errorText;
  }

  /**
   * Return the uid from the location header
   *
   * @function getUidFromLocation
   * @example response.getUidFromLocation();
   * @memberof module:core/response
   * @example http://backbee.net/rest/2/classcontent/Text/Paragraph/c9bf609ccdf0f3d5c63a542fd26632f3
   * @returns {null | string} uid
   */
  getUidFromLocation() {
    let locationHeader = this.getHeader('Location') || this.getHeader('location');
    let res;
    let regex;

    if (null === locationHeader) {
      return null;
    }

    regex = new RegExp('[\/]([a-f0-9]{32}$)');

    res = regex.exec(locationHeader);

    return (null !== res) ? res[1] : res;
  }

  /**
   * Get range from
   *
   * @function getRangeFrom
   * @example response.getRangeFrom();
   * @memberof module:core/response
   * @example Content-Range: 0-8/9
   * @returns {Numeric} range from
   */
  getRangeFrom() {
    let rangeHeader = this.getHeader('Content-Range') || this.getHeader('content-range');
    let res;

    if (null === rangeHeader) {
      return null;
    }

    res = rangeHeader.split('-');
    if (res[0] === undefined) {
        return null;
    }

    return parseInt(res[0], 10);
  }

  /**
   * Get range to
   *
   * @function getRangeTo
   * @memberof module:core/response
   * @example response.getRangeTo();
   * @example Content-Range: 0-8/9
   * @returns {Numeric} range to
   */
  getRangeTo() {
    let rangeHeader = this.getHeader('Content-Range') || this.getHeader('content-range');
    let res;
    let res2;

    if (null === rangeHeader) {
      return null;
    }

    res = rangeHeader.split('/');
    if (res[0] === undefined) {
      return null;
    }

    res2 = res[0].split('-');
    if (res2[1] === undefined) {
      return null;
    }

    return parseInt(res2[1], 10);
  }

  /**
   * Get range total
   *
   * @function getRangeTotal
   * @example response.getRangeTotal();
   * @memberof module:core/response
   * @example Content-Range: 0-8/9
   * @returns {Numeric} range total
   */
  getRangeTotal() {
    let rangeHeader = this.getHeader('Content-Range') || this.getHeader('content-range');
    let res;

    if (null === rangeHeader) {
      return null;
    }

    res = rangeHeader.split('/');
    if (res[1] === undefined) {
      return null;
    }

    return parseInt(res[1], 10);
  }

  /**
   * Set all headers as object
   *
   * @function setHeaders
   * @example response.setHeaders({
   *    'Content-Type': 'application/x-www-form-uriencoded',
   * })
   * @memberof module:core/response
   * @param {object} headers
   * @returns {Response} this
   */
  setHeaders(headers) {
    this.headers = headers;

    return this;
  }

  /**
   * Add one header by name and value
   *
   * @function addHeader
   * @memberof module:core/response
   * @example request.addHeader('Accept', 'application/json');
   * @param {string} name
   * @param {string} value
   * @returns {Response} this
   */
  addHeader(name, value) {
    this.headers[name] = value;

    return this;
  }

  /**
   * Set the data
   *
   * @function setData
   * @memberof module:core/response
   * @example request.setData({
   *    country: 'France,
   * });
   * @param {string} data
   * @returns {Response} this
   */
  setData(data) {
    this.data = data;

    return this;
  }

  /**
   * Set the raw data
   *
   * @function setRawData
   * @memberof module:core/response
   * @example response.setRawData('{"country": "France"}');
   * @param {string} rawData
   * @returns {Response} this
   */
  setRawData(rawData) {
    this.rawData = rawData;

    return this;
  }

  /**
   * Set the status code
   *
   * @function setStatus
   * @memberof module:core/response
   * @example response.setStatus(200);
   * @param {number} status
   * @returns {Response} this
   */
  setStatus(status) {
    this.status = status;

    return this;
  }

  /**
   * Set the status text
   *
   * @function setStatusText
   * @memberof module:core/response
   * @example response.setStatusText('200 No Content');
   * @param {string} statusText
   * @returns {Response} this
   */
  setStatusText(statusText) {
    this.statusText = statusText;

    return this;
  }

  /**
   * Set the error text
   *
   * @function setErrorText
   * @memberof module:core/response
   * @example response.setErrorText('500 Fatal error');
   * @param {string} errorText
   * @returns {Response} this
   */
  setErrorText(errorText) {
    this.errorText = errorText;

    return this;
  }
};
